import { Dispatch, SetStateAction, useState } from "react";

import { useTranslation } from "react-i18next";

import { AxiosError } from "axios";
import moment from "moment";
import { ApiResources } from "@/api/resources";
import Autocomplete from "@/components/Autocomplete/Autocomplete";
import MonthRangePicker from "@/components/DatePickers/MonthRangePicker/MonthRangePicker";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import { useFileDownload } from "@/components/Table/hooks";
import { creditsReportMinDate } from "@/Pages/Auth/constants";
import {
  CreditsReportPeriodTypes,
  GenerateReportPayload,
  PeriodType
} from "@/Pages/Dashboard/LeftSide/types";
import { inputFieldMarginBottom, useDateTime } from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import useResponse from "@/shared/hooks/useResponse";
import { Typography } from "@mui/material";

import { colors } from "@dotnet/telematics-design-system";

interface GenerateReportDialogProps {
  toMd: boolean;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const GenerateReportDialog = ({
  toMd,
  isOpen,
  setIsOpen
}: GenerateReportDialogProps) => {
  const { t } = useTranslation();

  const { handleCommonError } = useResponse();
  const { downloadFile } = useFileDownload();
  const { currentYear, currentMonth } = useDateTime();

  const initialPayload = {
    startMonth: currentMonth - 1,
    startYear: currentYear,
    endMonth: currentMonth - 1,
    endYear: currentYear
  };

  const dropdownOptions: CreditsReportPeriodTypes[] = [
    { value: PeriodType.Previous, name: t("Dialog##previous month") },
    { value: PeriodType.Custom, name: t("Dialog##custom period") }
  ];

  const [hasRange, setHasRange] = useState(true);
  const [payload, setPayload] = useState<GenerateReportPayload>(initialPayload);
  const [periodType, setPeriodType] = useState<CreditsReportPeriodTypes>(
    dropdownOptions[0]
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
    setPayload(initialPayload);
  };

  const isPreviousMonth = periodType.value === PeriodType.Previous;

  const { startYear, startMonth, endYear, endMonth } = payload;

  const handleGenerate = async () => {
    try {
      const params = new URLSearchParams({
        PreviousMonth: String(isPreviousMonth)
      });

      if (!isPreviousMonth) {
        params.append("StartYear", String(startYear));
        params.append("StartMonth", String(startMonth));
        params.append("EndYear", String(endYear));
        params.append("EndMonth", String(endMonth));
      }

      await downloadFile({
        noHeaders: true,
        passUrl: `${import.meta.env.VITE_BASE_URL}${ApiResources.CreditsReport}?${params}`,
        setIsLoading,
        onCloseDialog: handleClose
      });
    } catch (error) {
      handleCommonError(error as AxiosError, t);
    }
  };

  const handleDateChange = (range: { start: Date; end: Date } | null) => {
    if (!range) {
      setHasRange(false);
      setPayload(initialPayload);
      return;
    }

    setHasRange(true);

    const { start, end } = range;

    setPayload(prev => ({
      ...prev,
      startMonth: start.getMonth() + 1,
      startYear: start.getFullYear(),
      endMonth: end.getMonth() + 1,
      endYear: end.getFullYear()
    }));
  };

  const handlePeriodChange = (periodObj: CreditsReportPeriodTypes) => {
    setPeriodType(periodObj);
    if (periodObj.value === PeriodType.Previous) {
      setPayload(initialPayload);
      setHasRange(true);
    }
  };

  const minimumStartDate = moment(creditsReportMinDate);

  const checkIfSameMonth = (month: Date) =>
    moment(month).isSame(moment(), "month");

  return (
    <Dialog
      title={t("Dialog##credits history##generate report title")}
      description={t("Dialog##credits history##generate report description")}
      actions={
        <DialogActions
          onClose={handleClose}
          confirmationButtonText={t("Button##generate")}
          cancelButtonText={t("Button##close")}
          isLoading={isLoading}
          isreadyToConfirm={isPreviousMonth || hasRange}
        />
      }
      submit={handleGenerate}
      isTitleSeparator
      isActionsSeparator
      width={toMd ? undefined : DialogWidth.MinWidth}
      close={handleClose}
      open={isOpen}
    >
      <Autocomplete
        data-testid="choose-period-type-dropdown"
        fullWidth
        disableClearable
        isRequired
        size="medium"
        labelLeft={t("Dialog##choose period")}
        options={dropdownOptions}
        value={periodType}
        onChange={(_, value) => handlePeriodChange(value)}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, value) => option?.value === value}
        customStyle={inputFieldMarginBottom}
      />

      <Typography
        variant="body2Medium"
        sx={{ color: colors.textSecondary, marginTop: "16px" }}
      >
        {t("Dialog##date range")}*
      </Typography>

      <MonthRangePicker
        disabled={isPreviousMonth}
        shouldDisableMonth={checkIfSameMonth}
        minDate={minimumStartDate}
        maxDate={moment()}
        onChange={handleDateChange}
        customStyle={{ paddingTop: "4px" }}
        externalValue={
          hasRange
            ? {
                start: moment({
                  year: startYear,
                  month: startMonth - 1
                }).toDate(),
                end: moment({ year: endYear, month: endMonth - 1 }).toDate()
              }
            : null
        }
      />
    </Dialog>
  );
};

export default GenerateReportDialog;
